import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import {
  CategoryModel,
  CategoryService,
} from '@core/services/category.service';
import {
  ShareSettingCreatePayload,
  ShareSettingModel,
  ShareSettingService,
  ShareSettingUpdatePayload,
} from '@core/services/share-setting.service';
import {
  LanguageModel,
  LanguageService,
} from '@core/services/language.service';
import { MindMapService } from '@core/services/mindmap.service';
import { AiContentLanguageService } from '@core/services/ai-content-language.service';
import { UiTranslationService } from '@core/services/ui-translation.service';

@Component({
  selector: 'app-share-setting-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './share-setting-modal.html',
  styleUrl: './share-setting-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareSettingModal implements OnInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly fb = inject(FormBuilder);
  private readonly shareSettingService = inject(ShareSettingService);
  private readonly categoryService = inject(CategoryService);
  private readonly languageService = inject(LanguageService);
  private readonly aiContentLanguageService = inject(AiContentLanguageService);
  private readonly mindMapService = inject(MindMapService);
  private readonly toastr = inject(ToastrService);
  private readonly translations =
    inject(UiTranslationService).namespace('modals');
  readonly t = this.translations.t;

  @Input() mindMapId: number | null = null;
  @Input() workspaceId: number | null = null;
  @Input() mindMapTitle = 'New Mind Map';
  @Input() mindMapPublicId: string | null = null;

  readonly languages = signal<LanguageModel[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly categories = signal<CategoryModel[]>([]);
  readonly currentShare = signal<ShareSettingModel | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly copied = signal(false);
  readonly copyTab = signal<'link' | 'embed'>('link');
  private readonly destroyRef = inject(DestroyRef);
  private copyTimer?: ReturnType<typeof setTimeout>;
  private readonly categorySelect =
    viewChild<ElementRef<HTMLSelectElement>>('categorySelect');
  private readonly focusCategoryOnOpen = effect(() => {
    if (this.loading()) {
      return;
    }

    const categorySelect = this.categorySelect();
    if (!categorySelect) {
      return;
    }

    queueMicrotask(() => categorySelect.nativeElement.focus());
  });

  readonly form = this.fb.group({
    categoryId: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    languageCode: this.fb.control<string | null>(null, [Validators.required]),
    description: this.fb.control<string | null>(null, [
      Validators.maxLength(300),
    ]),
    allowCopy: this.fb.nonNullable.control(false),
    allowExport: this.fb.nonNullable.control(false),
  });

  readonly isPublished = computed(() => Boolean(this.currentShare()?.id));
  readonly publicLink = computed(() => {
    const value = this.currentShare()?.publicUrl;
    if (!value) return '';
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password
        ? url.href
        : '';
    } catch {
      return '';
    }
  });
  readonly canSharePublicLink = computed(() =>
    Boolean(this.currentShare()?.id && this.publicLink()),
  );
  readonly embedCode = computed(() => {
    const share = this.currentShare();
    if (!share?.id || share.allowEmbed !== true || !share.embedUrl) return '';
    try {
      const url = new URL(share.embedUrl);
      const segments = url.pathname.split('/');
      if (
        url.origin !== 'https://app.reazly.com' ||
        url.username ||
        url.password ||
        url.search ||
        url.hash ||
        segments.length !== 4 ||
        segments[1] !== 'viewer' ||
        !/^[A-Za-z0-9]{26}$/.test(segments[2]) ||
        !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(segments[3]) ||
        segments[3].length > 120 ||
        segments[2] !== this.mindMapPublicId ||
        segments[3] !== share.slug
      )
        return '';
      const escape = (value: string) =>
        value.replace(
          /[&<>"']/g,
          (char) =>
            ({
              '&': '&amp;',
              '<': '&lt;',
              '>': '&gt;',
              '"': '&quot;',
              "'": '&#39;',
            })[char]!,
        );
      return `<iframe src="${escape(url.href)}" title="${escape(this.mindMapTitle)}" width="100%" height="400" style="border:0" loading="lazy" referrerpolicy="no-referrer" sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"></iframe>`;
    } catch {
      return '';
    }
  });

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.copyTimer));
  }

  selectCopyTab(tab: 'link' | 'embed'): void {
    this.copyTab.set(tab);
    clearTimeout(this.copyTimer);
    this.copied.set(false);
  }

  copyEmbedCode(): void {
    void this.copyText(this.embedCode(), 'embed');
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  publish(): void {
    if (this.saving()) {
      return;
    }

    const mindMapId = this.mindMapId;
    const workspaceId = this.workspaceId;

    if (this.form.controls.categoryId.invalid) {
      this.form.controls.categoryId.markAsTouched();
      this.errorMessage.set(
        this.translations.tr('share.errorCategoryRequired'),
      );
      return;
    }
    if (
      this.form.controls.languageCode.invalid ||
      !this.languages().some(
        (language) => language.code === this.form.controls.languageCode.value,
      )
    ) {
      this.form.controls.languageCode.markAsTouched();
      this.errorMessage.set(
        this.translations.tr('share.errorLanguageRequired'),
      );
      return;
    }
    if (this.form.controls.description.hasError('maxlength')) {
      this.errorMessage.set(
        this.translations.tr('share.errorDescriptionMaxLength'),
      );
      return;
    }
    if (!mindMapId || !workspaceId) {
      this.errorMessage.set(this.translations.tr('share.errorInvalidContext'));
      return;
    }

    const nowIso = new Date().toISOString();
    const existingShare = this.currentShare();

    const basePayload: ShareSettingCreatePayload = {
      workspaceId,
      mindMapId,
      deckId: null,
      shareKey: existingShare?.shareKey ?? null,
      categoryId: this.form.controls.categoryId.value,
      languageCode: this.form.controls.languageCode.value,
      description: this.normalizeDescription(
        this.form.controls.description.value,
      ),
      publishedTitle: this.normalizePublishedTitle(this.mindMapTitle),
      allowCopy: this.form.controls.allowCopy.value,
      allowExport: this.form.controls.allowExport.value,
      allowEmbed: true,
      publishedAt: nowIso,
      publishedByUserId: null,
    };

    const request$ = existingShare
      ? this.shareSettingService.update({
          ...(basePayload satisfies ShareSettingCreatePayload),
          id: existingShare.id,
        } satisfies ShareSettingUpdatePayload)
      : this.shareSettingService.create(basePayload);

    this.errorMessage.set(null);
    this.saving.set(true);

    request$.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: (savedShare) => {
        this.currentShare.set(savedShare);
        this.toastr.success(this.translations.tr('share.linkPublished'));
        this.activeModal.close(savedShare);
      },
      error: (error: Error) => {
        this.errorMessage.set(
          error?.message ?? this.translations.tr('share.errorPublish'),
        );
      },
    });
  }

  unpublish(): void {
    if (this.saving()) {
      return;
    }

    const existingShare = this.currentShare();
    if (!existingShare?.id) {
      return;
    }

    this.errorMessage.set(null);
    this.saving.set(true);

    this.shareSettingService
      .delete(existingShare.id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.currentShare.set(null);
          this.copied.set(false);
          this.toastr.success(this.translations.tr('share.linkUnpublished'));
          this.activeModal.close();
        },
        error: (error: Error) => {
          this.errorMessage.set(
            error?.message ?? this.translations.tr('share.errorUnpublish'),
          );
        },
      });
  }

  copyPublicLink(): void {
    if (this.canSharePublicLink())
      void this.copyText(this.publicLink(), 'link');
  }

  private async copyText(
    text: string,
    target: 'link' | 'embed',
  ): Promise<void> {
    if (!text) return;
    this.errorMessage.set(null);
    try {
      await navigator.clipboard.writeText(text);
      if (this.destroyRef.destroyed) return;
      if (this.copyTab() === target) {
        clearTimeout(this.copyTimer);
        this.copied.set(true);
        this.copyTimer = setTimeout(() => this.copied.set(false), 1400);
      }
      this.toastr.success(
        this.translations.tr(
          target === 'embed' ? 'share.embedCopied' : 'share.linkCopied',
        ),
      );
    } catch {
      if (!this.destroyRef.destroyed)
        this.errorMessage.set(
          this.translations.tr(
            target === 'embed' ? 'share.errorCopyEmbed' : 'share.errorCopyLink',
          ),
        );
    }
  }

  shareOnLinkedIn(): void {
    this.openShareUrl('https://www.linkedin.com/sharing/share-offsite/?url=');
  }

  shareOnFacebook(): void {
    this.openShareUrl('https://www.facebook.com/sharer/sharer.php?u=');
  }

  shareOnInstagram(): void {
    if (!this.canSharePublicLink()) {
      return;
    }

    const link = this.publicLink();
    if (!link) {
      return;
    }

    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.toastr.success(this.translations.tr('share.linkCopiedInstagram'));
      })
      .catch(() => {
        this.errorMessage.set(this.translations.tr('share.errorCopyLink'));
      });
  }

  private openShareUrl(baseUrl: string): void {
    if (!this.canSharePublicLink()) {
      return;
    }

    const link = this.publicLink();
    if (!link) {
      return;
    }

    const encodedLink = encodeURIComponent(link);
    window.open(`${baseUrl}${encodedLink}`, '_blank', 'noopener,noreferrer');
  }

  private loadInitialData(): void {
    const mindMapId = this.mindMapId;
    if (!mindMapId) {
      this.errorMessage.set(this.translations.tr('share.errorInvalidContext'));
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    forkJoin({
      languages: this.languageService.list(),
      defaultLanguageCode: this.aiContentLanguageService.resolve(),
      mindMap: this.mindMapService.get(mindMapId),
      categories: this.categoryService
        .list()
        .pipe(catchError(() => of([] as CategoryModel[]))),
      shares: this.shareSettingService.list(),
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({
          categories,
          defaultLanguageCode,
          shares,
          languages,
          mindMap,
        }) => {
          const contentLanguages = languages.filter(
            (language) => language.isContentSupported && language.code,
          );
          this.languages.set(contentLanguages);
          const orderedCategories = [...categories]
            .filter((category) => category.isActive)
            .sort(
              (a, b) =>
                a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
            );

          this.categories.set(orderedCategories);

          const existingShare = this.shareSettingService.findByMindMapId(
            shares,
            mindMapId,
          );
          this.currentShare.set(existingShare);

          if (!existingShare) {
            this.form.controls.languageCode.setValue(
              this.resolveInitialLanguageCode(
                contentLanguages,
                defaultLanguageCode,
                mindMap.languageCode,
              ),
            );
            return;
          }

          this.form.patchValue({
            categoryId: existingShare.categoryId,
            languageCode:
              existingShare.languageCode ?? mindMap.languageCode ?? null,
            description: existingShare.description,
            allowCopy: existingShare.allowCopy,
            allowExport: existingShare.allowExport,
          });
        },
        error: (error: Error) => {
          this.errorMessage.set(
            error?.message ?? this.translations.tr('share.errorLoadSettings'),
          );
        },
      });
  }

  private normalizeDescription(value: string | null): string | null {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return null;
    }

    return normalized.length <= 300 ? normalized : normalized.slice(0, 300);
  }

  private resolveInitialLanguageCode(
    languages: LanguageModel[],
    defaultLanguageCode: string | null,
    mindMapLanguageCode: string | null | undefined,
  ): string | null {
    for (const candidate of [defaultLanguageCode, mindMapLanguageCode]) {
      if (languages.some((language) => language.code === candidate)) {
        return candidate ?? null;
      }
    }

    return null;
  }

  private normalizePublishedTitle(
    value: string | null | undefined,
  ): string | null {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return null;
    }

    return normalized.length <= 120 ? normalized : normalized.slice(0, 120);
  }
}
